// The canonical `ThirdPartyConfig` interface lives in
// `@atlassian/rovo-connectors-config`. It is consumed here to build the derived
// `ThirdPartyConfigsWithAPIResponse` type below.
import type { ThirdPartyConfig } from '@atlassian/rovo-connectors-config/schemas/types';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { type RovoLogo } from '../icons';

import { ConnectorSources } from './connector-sources';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { ConnectorSources } from './connector-sources';

/**
 * Type for Platform Integration ARI.
 * The API always returns integrationId in this format.
 */
export type PlatformIntegrationAri = `ari:cloud:platform::integration/${string}`;

export const SMARTLINKS_RESOURCE_TYPE_PREFIX = 'smartlinks-connector';
// Mirrors LITE_CONNECTOR_SOURCE_PREFIX in rovo-connectors-config (same value/name) to avoid
// divergent names for the same concept across the two parallel connector-source derivations.
export const LITE_CONNECTOR_SOURCE_PREFIX = 'lite-connector-';

export const FEDERATED_CONNECTOR_SOURCE: 'federated' = ConnectorSources.Federated;
export const LITE_CONNECTOR_SOURCE: 'lite-connector' = ConnectorSources.Lite;
export const SMARTLINKS_CONNECTOR_SOURCE: 'smartlinks' = ConnectorSources.Smartlinks;
export const FULL_CONNECTOR_SOURCE: 'full-connector' = ConnectorSources.Full;

/**
 * authResolutionMode value indicating the admin has imported the user's identity,
 * so read/search access works without the user completing personal OAuth.
 */
export const AUTH_RESOLUTION_MODE_ADMIN_IMPORTED = 'ADMIN_IMPORTED_USER_LINK' as const;
export const BACKFILL_CONNECTOR_SOURCE: 'backfill' = ConnectorSources.Backfill;

export type ConnectorSource = (typeof ConnectorSources)[keyof typeof ConnectorSources];

export const isConnectorSource: (value: string | null | undefined) => value is ConnectorSource = (
	value: string | null | undefined,
): value is ConnectorSource => {
	return Object.values(ConnectorSources).includes(value as ConnectorSource);
};

/**
 * Prefix used for all dynamically loaded Forge connector product keys.
 * All Forge connectors will have an id in the format: forge-connector-{uniqueId}-{connectorName}
 */
export const FORGE_CONNECTOR_PREFIX = 'forge-connector-';

/**
 * Type alias for Forge connector product keys.
 *
 * Note: Using `string` instead of template literal type `forge-connector-${string}` to avoid
 * TypeScript issues with Object.fromEntries() and dynamic object construction. Runtime validation
 * is still enforced via isForgeConnectorProductKey() type guard.
 */
export type ForgeConnectorProductKey = string;

/**
 * Type guard to check if a product key is a Forge connector.
 * All dynamic Forge connectors will have an id starting with 'forge-connector-'.
 */
export const isForgeConnectorProductKey: (type: string) => type is ForgeConnectorProductKey = (
	type: string,
): type is ForgeConnectorProductKey => {
	return type.startsWith(FORGE_CONNECTOR_PREFIX);
};

/**
 * Runtime registry for Forge connector icons.
 * Similar to ThirdPartyConfigs, but populated dynamically from API responses.
 */
const forgeConnectorIconsRegistry = new Map<ForgeConnectorProductKey, RovoLogo>();

/**
 * Register an icon for a Forge connector product key.
 * Called when processing Forge connectors from the API.
 */
export const registerForgeConnectorIcon: (
	productKey: ForgeConnectorProductKey,
	icon: RovoLogo,
) => void = (productKey: ForgeConnectorProductKey, icon: RovoLogo): void => {
	forgeConnectorIconsRegistry.set(productKey, icon);
};

/**
 * Get the icon for a Forge connector product key.
 * Returns undefined if the icon hasn't been registered yet.
 */
export const getForgeConnectorIcon: (
	productKey: ForgeConnectorProductKey,
) => RovoLogo | undefined = (productKey: ForgeConnectorProductKey): RovoLogo | undefined => {
	return forgeConnectorIconsRegistry.get(productKey);
};

/**
 * Clear all registered Forge connector icons.
 * Useful for testing or when switching users/clouds.
 */
export const clearForgeConnectorIcons: () => void = (): void => {
	forgeConnectorIconsRegistry.clear();
};

// certain properties from ThirdPartyConfig are redefined as they are guaranteed
export type ThirdPartyConfigsWithAPIResponse = ThirdPartyConfig & {
	providerId: string;
	outboundAuthUrl: string;
	userNeedsOAuth: boolean;
	type?: string;
	hasAuthUpgrade?: boolean | null;
	adminAuthOverrideApplied?: boolean;
	workspaceName?: string;
	connectionId?: string;
	connectorSources?: ConnectorSource[];
	workspaceUrl?: string;
	datasourceId?: string;
	datasources?: ThirdPartyConfigsWithAPIResponse[];
	// Whether the user has previously completed OAuth for this datasource.
	// This is intentionally optional because older API responses might not include it.
	impersonationEnabled?: boolean;
	authResolutionMode?: string;
	connectorCreatedAt?: string;
};

export type ThirdPartyConfigsBootstrap = Partial<
	Record<ProductKeys3P | ForgeConnectorProductKey, ThirdPartyConfigsWithAPIResponse>
>;
